/**
 * LoanTabCanvas — the root wrapper for a Loan Drilldown tab.
 *
 * One job: own the inset between the tab switcher above and the first thing the
 * tab renders. Every tab gets the same gap, so switching tabs doesn't nudge the
 * content up or down.
 *
 * The spacing is deliberately the Properties / Applicants spacing (those tabs are
 * the reference): a 36px block inset and an 8px side inset inside the standard
 * max-width container. That's what `LoanDrilldownTabContentWrapper` has been
 * shipping as `py={4.5} px={1}`, promoted here to a named token so the two paths
 * can't drift apart.
 *
 * Normally you get this for free via `LoanTabShell`. Use it directly only for a
 * tab that manages its own header — and then still use it, rather than writing
 * your own padding.
 */
import { Container } from "@mui/material";
import PropTypes from "prop-types";
import { memo, useMemo } from "react";

import { LOAN_TAB_MAX_WIDTH } from "./loanTabLayoutTokens";
import { TAB_TOKENS as T } from "./tabTokens";

const LoanTabCanvas = ({
  children,
  maxWidth = LOAN_TAB_MAX_WIDTH,
  py,
  px,
  sx,
  ...rest
}) => {
  const styles = useMemo(
    () => ({
      paddingTop: py ?? T.space.canvasPy,
      paddingBottom: py ?? T.space.canvasPy,
      paddingLeft: px ?? T.space.canvasPx,
      paddingRight: px ?? T.space.canvasPx,
      [T.bp.twoUp]: {
        paddingTop: py ?? T.space.canvasPyTight,
        paddingBottom: py ?? T.space.canvasPyTight,
        paddingLeft: px ?? T.space.canvasPxTight,
        paddingRight: px ?? T.space.canvasPxTight,
      },
      ...sx,
    }),
    [py, px, sx],
  );

  return (
    <Container maxWidth={maxWidth} sx={styles} {...rest}>
      {children}
    </Container>
  );
};

LoanTabCanvas.propTypes = {
  children: PropTypes.node,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /** Override the block inset. Only for a tab that genuinely can't use the standard. */
  py: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Override the side inset. Same caveat. */
  px: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sx: PropTypes.object,
};

export default memo(LoanTabCanvas);
